import { Fragment, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Container, Input } from "reactstrap";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { getWaitlistAnalytics, fetchBranches } from "../../../utils/apiCalls";
import { C, tbl, reportingGlobalCSS } from "../../reporting/reportingStyles";
import { GradientDefs, ChartTooltip, Tile, RCard, Loader } from "../../reporting/ReportingShared";
import Breadcrumb from "../../common/breadcrumb";

const today = () => new Date().toISOString().split("T")[0];
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split("T")[0]; };

const DATE_PRESETS = [
  { label: "Last 7 Days", value: "7d", range: () => [daysAgo(7), today()] },
  { label: "Last 30 Days", value: "30d", range: () => [daysAgo(30), today()] },
  { label: "Last 90 Days", value: "90d", range: () => [daysAgo(90), today()] },
  { label: "Custom", value: "custom", range: () => [null, null] },
];

const DOW_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const formatHour = (h) => {
  if (h === 0) return "12 AM";
  if (h < 12) return `${h} AM`;
  if (h === 12) return "12 PM";
  return `${h - 12} PM`;
};

const WaitlistAnalyticsPage = () => {
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = (userType === "merchant" || userType === "waitlist_staff") ? merchantId : reduxSelectedMerchant;

  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [datePreset, setDatePreset] = useState("30d");
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      const response = await fetchBranches({}, false);
      let list = response?.data || [];
      if (selectedMerchant && selectedMerchant !== "all") list = list.filter((b) => String(b.MerchantId) === String(selectedMerchant));
      if (userType === "waitlist_staff") {
        const assigned = JSON.parse(localStorage.getItem("assignedBranchIds") || "[]").map(String);
        list = list.filter((b) => assigned.includes(String(b.id)));
      }
      setBranches(list);
    };
    load();
  }, [selectedMerchant, userType]);

  const fetchData = useCallback(async () => {
    if (!selectedMerchant || selectedMerchant === "all") { setData(null); return; }
    setLoading(true);
    const params = { MerchantId: selectedMerchant, startDate, endDate };
    if (selectedBranch) params.branchId = selectedBranch;
    const result = await getWaitlistAnalytics(params);
    setData(result);
    setLoading(false);
  }, [selectedMerchant, selectedBranch, startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePresetChange = (value) => {
    setDatePreset(value);
    const preset = DATE_PRESETS.find((p) => p.value === value);
    if (preset && preset.value !== "custom") {
      const [s, e] = preset.range();
      setStartDate(s);
      setEndDate(e);
    }
  };

  // Build heatmap matrix: rows = day of week, cols = hour
  const peakHours = data?.peakHours || [];
  const maxPeakCount = peakHours.reduce((m, p) => Math.max(m, p.count), 0);
  const heatmapValue = (dow, hour) => peakHours.find((p) => p.dayOfWeek === dow && p.hour === hour)?.count || 0;
  const heatmapColor = (count) => {
    if (count === 0) return "transparent";
    const intensity = Math.min(1, count / Math.max(1, maxPeakCount));
    // Coral gradient — light to saturated
    return `rgba(0, 150, 137, ${0.1 + intensity * 0.7})`;
  };

  const kpis = data?.kpis;
  const tiles = kpis ? [
    { label: "Total Entries", value: kpis.totalEntries, gradient: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, icon: "📋", delay: 0 },
    { label: "Avg Quoted", value: `${kpis.avgQuotedMinutes}m`, gradient: `linear-gradient(135deg, ${C.blue}, #A8B8FF)`, icon: "⏱", delay: 40 },
    { label: "Avg Actual", value: `${kpis.avgActualMinutes}m`, gradient: `linear-gradient(135deg, ${C.green}, #38F9D7)`, icon: "✓", delay: 80 },
    {
      label: "Avg Overshoot",
      value: `${kpis.avgOvershootMinutes >= 0 ? "+" : ""}${kpis.avgOvershootMinutes}m`,
      sub: kpis.avgOvershootMinutes > 0 ? "longer than quoted" : kpis.avgOvershootMinutes < 0 ? "shorter than quoted" : "on the money",
      gradient: `linear-gradient(135deg, ${C.orange}, #FFD200)`,
      icon: kpis.avgOvershootMinutes > 0 ? "⚠" : "🎯",
      delay: 120,
    },
    { label: "Seated", value: `${kpis.seatedRate}%`, gradient: `linear-gradient(135deg, ${C.green}, #38F9D7)`, icon: "✓", delay: 160 },
    { label: "Abandoned", value: `${kpis.abandonmentRate}%`, gradient: `linear-gradient(135deg, ${C.textLight}, #B0B0B0)`, icon: "↩", delay: 200 },
    { label: "No-Show", value: `${kpis.noShowRate}%`, gradient: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, icon: "✕", delay: 240 },
  ] : [];

  const hourlyChartData = (data?.hourlyCounts || []).map((h) => ({ name: formatHour(h.hour), count: h.count }));

  return (
    <Fragment>
      <style>{reportingGlobalCSS}</style>
      <Breadcrumb title="Waitlist Analytics" parent="Table Management" />
      <Container fluid className="rpt-wrap">
        {/* Filters */}
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
          <Input type="select" value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)} style={{ maxWidth: 200, borderRadius: 8, border: `1px solid ${C.border}` }}>
            <option value="">All Branches</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Input>
          <Input type="select" value={datePreset} onChange={(e) => handlePresetChange(e.target.value)} style={{ maxWidth: 180, borderRadius: 8, border: `1px solid ${C.border}` }}>
            {DATE_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </Input>
          <Input type="date" value={startDate || ""} onChange={(e) => { setStartDate(e.target.value); setDatePreset("custom"); }} style={{ maxWidth: 160, borderRadius: 8, border: `1px solid ${C.border}` }} />
          <Input type="date" value={endDate || ""} onChange={(e) => { setEndDate(e.target.value); setDatePreset("custom"); }} style={{ maxWidth: 160, borderRadius: 8, border: `1px solid ${C.border}` }} />
        </div>

        {(!selectedMerchant || selectedMerchant === "all") ? (
          <RCard><p style={{ textAlign: "center", padding: 40, color: C.textLight }}>Select a merchant</p></RCard>
        ) : loading ? (
          <Loader />
        ) : !data || kpis.totalEntries === 0 ? (
          <RCard><p style={{ textAlign: "center", padding: 40, color: C.textLight }}>No waitlist data in this window</p></RCard>
        ) : (
          <>
            {/* KPI Tiles */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 20 }}>
              {tiles.map((t) => <Tile key={t.label} {...t} />)}
            </div>

            {/* Hourly Distribution */}
            <RCard title="Joins by hour of day" style={{ marginBottom: 20 }}>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={hourlyChartData}>
                  <GradientDefs />
                  <CartesianGrid strokeDasharray="3 3" stroke={C.borderLight} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: C.textMed }} interval={1} />
                  <YAxis tick={{ fontSize: 11, fill: C.textMed }} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="count" fill={C.coral} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </RCard>

            {/* Peak Hours Heatmap */}
            <RCard title="Peak hours by day of week">
              <div style={{ overflowX: "auto" }}>
                <table style={{ ...tbl.table, borderCollapse: "separate", borderSpacing: 2 }}>
                  <thead>
                    <tr>
                      <th style={{ ...tbl.th, position: "sticky", left: 0, background: C.white, zIndex: 1 }}> </th>
                      {Array.from({ length: 24 }, (_, h) => (
                        <th key={h} style={{ ...tbl.th, fontSize: 10, textAlign: "center", minWidth: 28 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {DOW_LABELS.map((label, dow) => (
                      <tr key={dow}>
                        <td style={{ ...tbl.td, fontWeight: 700, fontSize: 12, color: C.textMed, position: "sticky", left: 0, background: C.white }}>{label}</td>
                        {Array.from({ length: 24 }, (_, hour) => {
                          const count = heatmapValue(dow, hour);
                          return (
                            <td
                              key={hour}
                              title={`${label} ${formatHour(hour)} — ${count} join${count === 1 ? "" : "s"}`}
                              style={{
                                ...tbl.td,
                                background: heatmapColor(count),
                                textAlign: "center",
                                fontSize: 10,
                                fontWeight: count > 0 ? 700 : 400,
                                color: count > maxPeakCount * 0.6 ? C.white : C.textMed,
                                minWidth: 28,
                                padding: 6,
                              }}
                            >
                              {count > 0 ? count : ""}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p style={{ fontSize: 11, color: C.textLight, marginTop: 8 }}>Darker = busier. Hover any cell for exact count.</p>
            </RCard>
          </>
        )}
      </Container>
    </Fragment>
  );
};

export default WaitlistAnalyticsPage;
